/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.16.0
 */

import { Fragment, useCallback } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import Title from '@components/common/Title/Title';
import { EBasketType } from '@modules/app/appConstants';
import withAsyncStateManagement from '@modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectTranslationWizardImportPersonalData } from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { IWizardImportPersonalDataTranslation } from '@modules/internationalization/types/wizards/importPersonalData';
import Wizard from '@modules/wizard/components/Wizard';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { IWizardState } from '@modules/wizard/wizardReducer';
import { makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { IRootReducer } from '@reducers/rootReducer';

import { wizardImportPersonalDataHandleNextAction } from '../wizardImportPersonalDataActions';
import { EWizardImportPersonalDataSteps } from '../wizardImportPersonalDataConstant';
import wizardImportPersonalDataReducer, { IWizardImportPersonalDataReducer } from '../wizardImportPersonalDataReducer';
import { makeSelectWizardImportPersonalDataValidation } from '../wizardImportPersonalDataSelectors';
import WizardImportPersonalDataConfirmation from './WizardImportPersonalDataConfirmation';
import WizardImportPersonalDataContentValidationStep from './WizardImportPersonalDataContentValidationStep';
import WizardImportPersonalDataDocumentSelectionStep from './WizardImportPersonalDataDocumentSelectionStep';
import WizardImportPersonalDataTitleElements from './WizardImportPersonalDataTitleElements';

const WizardImportPersonalData = (props: IConnectedProps & IDispatchProps) => {

    const {
        stepName,
        translation,
        validation,

        handleNext,
    } = props;

    const renderStep = useCallback(() => {
        switch (stepName) {
            case EWizardImportPersonalDataSteps.DocumentSelectionStep:
                return <WizardImportPersonalDataDocumentSelectionStep/>;
            case EWizardImportPersonalDataSteps.ContentValidationStep:
                return <WizardImportPersonalDataContentValidationStep/>;
            case EWizardImportPersonalDataSteps.ConfirmImportStep:
                return <WizardImportPersonalDataConfirmation/>;
        }
    }, [stepName]);

    return (
        <Fragment>
            <PageLocale lazy
                        fileNames={[
                            TranslationFileNamesEnum.wizardImportPersonalData,
                            TranslationFileNamesEnum.wizard,
                        ]}/>

            <Title pageName={ECommonTranslationPages.wizardImportPersonalData}/>

            <Wizard
                basketType={EBasketType.MOBILE}
                isNextDisabled={!validation?.isValid}
                name={EWizardName.IMPORT_PERSONAL_DATA}
                titleElements={
                    <WizardImportPersonalDataTitleElements
                        title={translation.ConfirmImportStep.exportButton}
                    />
                }
                translationProps={{
                    error: translation?.error,
                    steps: translation?.steps,
                    title: translation?.title,
                }}
                validateError={validation?.error}
                onWizardNextHandler={handleNext}
            >
                {renderStep()}
            </Wizard>

        </Fragment>
    );
};

interface IConnectedProps extends Pick<IWizardState, 'stepName'>, Pick<IWizardImportPersonalDataReducer, 'validation'> {
    translation: IWizardImportPersonalDataTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    stepName: makeSelectWizardStepName(),
    translation: makeSelectTranslationWizardImportPersonalData(),
    validation: makeSelectWizardImportPersonalDataValidation(),
});

export interface IDispatchProps {
    handleNext: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(wizardImportPersonalDataHandleNextAction()),
});

export default withAsyncStateManagement(
    connect(mapStateToProps, mapDispatchToProps)(WizardImportPersonalData),
    { reducer: { wizardImportPersonalData: wizardImportPersonalDataReducer } },
);
